// Shell fur. The body is drawn once as the skin layer, then N more times, each copy pushed out along
// the rest-pose normal and combed towards the tail. Each shell keeps only the hair cells tall enough
// to reach it, tapering towards the tips. Soft coverage blends over the solid body's depth so
// tiny gaps between hairs do not become dark screen-space occlusion pits.
// Colour comes from the baked coat maps (coatMaterial.ts).
//
// All shells of all cats share two shader programs (skin layer, shell); per-cat values are uniforms.
import { Color, MeshPhysicalMaterial, MeshStandardMaterial, type Texture, Vector3 } from 'three';
import type { CoatSpec } from '../../data/cats';
import { FUR_LENGTH_RANGE, MPU_RANGE, NOISE_GLSL } from './coatMaterial';

export interface FurSettings {
  colour: Texture;
  info: Texture;
  coat: CoatSpec;
}

interface Shared {
  uInfo: { value: Texture };
  uLen: { value: number };
  uTickDark: { value: Vector3 };
  uDensity: { value: number };
  uRim: { value: Vector3 };
  uShells: { value: number };
}

/** Rest-pose comb direction: back towards the tail, down the legs, along the tail. */
const COMB_GLSL = /* glsl */ `
vec3 furComb(vec3 p){
  vec3 back = vec3(0.0, -0.3, -1.0);
  vec3 down = vec3(0.0, -1.0, -0.15);
  float legs = smoothstep(0.15, 0.08, p.y) * step(-0.27, p.z);
  float face = smoothstep(0.24, 0.3, p.z);
  vec3 c = mix(back, down, legs);
  c = mix(c, vec3(0.0, 0.25, -1.0), face);
  return normalize(c);
}`;

function linear(hex: string): Vector3 {
  const c = new Color(hex);
  return new Vector3(c.r, c.g, c.b);
}

/** The amber rim the sunset light puts round the fur; the environment may retune it. */
export const FUR_RIM = new Vector3(0.95, 0.62, 0.36).multiplyScalar(0.55);

export class FurMaterials {
  readonly skin: MeshPhysicalMaterial;
  /** shells[i] is shell i + 1 of maxShells. */
  readonly shells: MeshStandardMaterial[] = [];
  private shared: Shared;

  constructor(s: FurSettings, readonly maxShells: number) {
    this.shared = {
      uInfo: { value: s.info },
      uLen: { value: s.coat.furLength },
      uTickDark: { value: linear(s.coat.dark) },
      uDensity: { value: 1400 }, // hair cells per metre of skin
      uRim: { value: FUR_RIM },
      uShells: { value: maxShells },
    };
    this.skin = this.makeSkin(s.colour);
    for (let i = 1; i <= maxShells; i++) this.shells.push(this.makeShell(s.colour, i));
  }

  /** Low tier: no shells, so the skin layer carries the whole coat, with a soft sheen. */
  setShellsVisible(n: number): void {
    this.shared.uShells.value = n;
    const bare = n === 0;
    if ((this.skin.sheen > 0) !== bare) {
      this.skin.sheen = bare ? 0.7 : 0;
    }
  }

  dispose(): void {
    this.skin.dispose();
    for (const m of this.shells) m.dispose();
  }

  private makeSkin(colour: Texture): MeshPhysicalMaterial {
    const m = new MeshPhysicalMaterial({ map: colour, roughness: 0.85, metalness: 0, sheen: 0, sheenRoughness: 0.55 });
    m.sheenColor.set('#d8c8b8');
    const shared = this.shared;
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, { uInfo: shared.uInfo, uShells: shared.uShells, uRim: shared.uRim });
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D uInfo; uniform float uShells; uniform vec3 uRim;')
        .replace('#include <map_fragment>', `#include <map_fragment>
          vec4 furInfo = texture2D(uInfo, vMapUv);
          // under the fur the skin layer reads as the shadowed roots; bare skin keeps its colour
          float hasFur = smoothstep(0.02, 0.1, furInfo.r) * step(0.5, uShells);
          diffuseColor.rgb *= mix(1.0, 0.98, hasFur);`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
          roughnessFactor = mix(roughnessFactor, 0.42, furInfo.b);`)
        .replace('#include <opaque_fragment>', `
          float furRim = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 3.0);
          outgoingLight += diffuseColor.rgb * uRim * furRim * (1.0 - furInfo.b) * (1.0 - step(0.5, uShells) * 0.6);
          #include <opaque_fragment>`);
    };
    m.customProgramCacheKey = () => 'cat-fur-skin';
    return m;
  }

  private makeShell(colour: Texture, index: number): MeshStandardMaterial {
    const m = new MeshStandardMaterial({
      map: colour, roughness: 0.9, metalness: 0,
      transparent: true, depthWrite: false,
    });
    const shared = this.shared;
    const uIndex = { value: index };
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, { ...shared, uIndex });
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', `#include <common>
          uniform sampler2D uInfo; uniform float uIndex; uniform float uShells; uniform float uLen;
          varying vec3 vRest;
          ${COMB_GLSL}`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          vRest = position;
          float uH = uIndex / max(uShells, 1.0);
          float furLen = textureLod(uInfo, uv, 0.0).r * ${FUR_LENGTH_RANGE.toFixed(2)} * uLen;
          vec3 cn = normalize(normal);
          vec3 comb = furComb(position);
          comb = normalize(comb - cn * dot(comb, cn));
          // straight out at the root, laid down along the comb and pulled by gravity at the tip
          transformed += (cn * uH + comb * uH * uH * 0.85 + vec3(0.0, -0.25, 0.0) * uH * uH) * furLen;`);
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>
          uniform sampler2D uInfo; uniform float uIndex; uniform float uShells; uniform float uDensity;
          uniform vec3 uTickDark; uniform vec3 uRim;
          varying vec3 vRest;
          ${NOISE_GLSL}`)
        .replace('#include <map_fragment>', `
          float uH = uIndex / max(uShells, 1.0);
          vec4 furInfo = texture2D(uInfo, vMapUv);
          float fur = furInfo.r * ${FUR_LENGTH_RANGE.toFixed(2)};
          if (fur < 0.04) discard;
          // hair cells never get much smaller than a pixel: far away they merge into clumps,
          // which keeps the fur from turning into shimmering noise
          // hair cells of about the same size on the body (uDensity = cells per metre), in power-of-two
          // steps so neighbouring regions share a grid
          float mpu = furInfo.a * ${MPU_RANGE.toFixed(1)};
          float cellsPerUv = exp2(floor(log2(max(mpu * uDensity, 64.0)) + 0.5));
          vec2 suv0 = vMapUv * cellsPerUv;
          float px = max(fwidth(suv0.x), fwidth(suv0.y));
          float level = ceil(log2(max(1.0, px * 1.1)));
          vec2 suv = suv0 / exp2(level);
          // far away the hairs fill their cells, so the coat reads as soft plush instead of noise
          float cover = 0.12 + smoothstep(0.25, 1.2, px) * 0.28;
          vec2 cell = floor(suv);
          vec2 f = fract(suv) - 0.5;
          vec2 jit = vec2(cdHash2(cell + 7.1), cdHash2(cell + 3.3)) - 0.5;
          float hair = 0.9 + 0.1 * cdHash2(cell);
          float t = uH / hair;                       // 0 at the root, 1 at this hair's tip
          float d = length(f - jit * 0.35 * (1.0 - cover * 2.0));
          float radius = (1.0 - t * (1.0 - cover)) * 0.48 + cover;
          float aa = max(fwidth(d), 0.015);
          float coverage = 1.0 - smoothstep(radius - aa, radius + aa, d);
          coverage *= 1.0 - smoothstep(hair - 0.04, hair + 0.04, uH);
          if (coverage < 0.01) discard;
          #include <map_fragment>
          // The opaque skin supplies depth and AO. Shells add colour and a soft silhouette,
          // rather than writing a different surface depth at every tiny hair-cell boundary.
          diffuseColor.a *= coverage * mix(0.65, 0.3, smoothstep(0.25, 1.0, uH));
          // agouti ticking: each hair is banded light and dark, with a dark tip
          // banding is finer than a pixel at a distance: then only its average darkening remains
          float tick = furInfo.g * (1.0 - smoothstep(0.35, 1.0, px));
          diffuseColor.rgb *= 1.0 - 0.04 * furInfo.g;
          float lightBand = smoothstep(0.25, 0.4, t) * smoothstep(0.62, 0.5, t) + smoothstep(0.68, 0.75, t) * smoothstep(0.88, 0.8, t);
          float darkBand = smoothstep(0.48, 0.55, t) * smoothstep(0.7, 0.64, t) + smoothstep(0.86, 0.94, t);
          diffuseColor.rgb *= 1.0 + 0.08 * lightBand * tick;
          diffuseColor.rgb = mix(diffuseColor.rgb, uTickDark, darkBand * tick * 0.16);
          // the fur shades its own roots
          diffuseColor.rgb *= mix(0.98, 1.02, smoothstep(0.0, 0.85, uH));`)
        .replace('#include <opaque_fragment>', `
          float furRim = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 2.5);
          outgoingLight += diffuseColor.rgb * uRim * furRim * (0.35 + 0.65 * uH);
          #include <opaque_fragment>`);
    };
    m.customProgramCacheKey = () => 'cat-fur-shell-soft-coverage';
    return m;
  }
}
